<template>
  <div class="accountNumberContainer-wrapper" v-if="address">
    <fieldset class="accountNumberContainer">
      <legend class="labelInDiv smallText gray">{{ labelText }}</legend>
      <InlineActionFeedback
        class="row alignCentreRow accountNumber fullWidth justifyToStartEnd"
        :action="copyAddress"
        :aria-label="`Copy ${labelText.replace(/:$/, '').toLowerCase()}`"
        :confirmed-message="copyConfirmedMessage"
        error-message="Copy failed"
        @click.stop
      >
        <div class="row alignCentreRow noGap fullWidth justifyToStartEnd">
          <span>{{ formattedAddress }}</span>
          <svg class="icon compactImg" aria-hidden="true">
            <use href="@/assets/icons/iconset.svg#copy_compact"></use>
          </svg>
        </div>
      </InlineActionFeedback>
    </fieldset>
  </div>
</template>

<script>
import { computed } from "vue";
import InlineActionFeedback from "@/components/GenericComponents/InlineActionFeedback.vue";
import { copyTextToClipboard } from "@/utils/clipboard";

export default {
  name: "MetaMaskAccountNumber",
  components: { InlineActionFeedback },
  props: {
    address: {
      type: String,
      required: true,
    },
    network: {
      type: String,
      default: "",
    },
    showBlockchainLinks: {
      type: Boolean,
      default: false,
    },
    // When false, display the full string without truncation (used for API key secrets)
    truncate: {
      type: Boolean,
      default: true,
    },
    labelText: {
      type: String,
      default: "Address:",
    },
    copyConfirmedMessage: {
      type: String,
      default: "Copied",
    },
  },
  setup(props) {
    const formattedAddress = computed(() => {
      if (!props.address) return "";
      // Format for display - preserve first 6 and last 4 characters
      if (props.truncate && props.address.length > 10) {
        return `${props.address.substring(0, 6)}...${props.address.substring(
          props.address.length - 4
        )}`;
      }
      return props.address;
    });

    const copyAddress = () => copyTextToClipboard(props.address);

    return {
      formattedAddress,
      copyAddress,
    };
  },
};
</script>

<style scoped>
.accountNumberContainer-wrapper {
  display: flex;
  flex-direction: column;
  width: min(100%, 18rem);
  min-width: 0;
  gap: var(--space-4);
  border-radius: var(--radius-sm);
  background: transparent;
}

.accountNumberContainer {
  cursor: pointer !important;
  display: block;
  border-radius: var(--radius-sm); /* Smaller radius for inside card */
  font-size: 0.8rem;
  height: calc(var(--control-height-compact) * 0.85);
  min-height: calc(var(--control-height-compact) * 0.85);
  min-width: 0;
  margin: 0;
  width: 100%;
  background: transparent;
  border: 1px solid var(--color-border-subtle);
  padding: 0 var(--space-3);
}

.labelInDiv {
  background: transparent;
  margin: 0 auto;
  padding: 0 var(--space-4);
  font-size: 0.7rem;
  color: var(--color-text-secondary);
}

.icon {
  margin-left: var(--space-3);
}

.accountNumber.inline-action-feedback {
  width: 100%;
  min-height: 0;
  border-radius: 0;
  line-height: 1.2;
}

.accountNumber span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metamask-icon {
  font-size: 1.2rem;
}

/* Blockchain links styling */
.blockchain-links {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  margin-top: var(--space-1);
  justify-content: center;
}

.blockchain-link {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-4);
  border-radius: var(--radius-sm);
  text-decoration: none;
  color: var(--color-text-emphasis);
  background-color: var(--color-surface-muted);
  transition: background-color 0.2s ease, transform 0.2s ease;
  font-size: 0.8rem;
}

.blockchain-link:hover {
  background-color: var(--color-surface-interactive-active);
  transform: translateY(-1px);
}

.blockchain-icon {
  font-size: 0.9rem;
}

.blockchain-name {
  font-size: 0.8rem;
}
</style>
